import React, { useState, useEffect } from "react";
import api, { HR_BASE_URL } from "../api";
import styled from "styled-components";
import { PlusCircle, List, FileText } from "lucide-react";

const Page = styled.div`
  min-height: 100vh;
  padding: clamp(20px, 4vw, 40px);
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 32px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(24px, 3vw, 32px);
  font-weight: 800;
  background: linear-gradient(135deg, var(--primary), var(--accent));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Subtitle = styled.p`
  margin: 8px 0 0 0;
  color: var(--muted);
  font-size: 14px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 20px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormCard = styled.div`
  background: var(--glass);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 24px;
  box-shadow: var(--shadow);
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: var(--text);
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: rgba(255, 255, 255, 0.05);
  color: var(--text);
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: rgba(255, 255, 255, 0.05);
  color: var(--text);
  min-height: 100px;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 14px;
  border-radius: 8px;
  background: var(--primary);
  color: white;
  border: none;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  &:hover {
    background: var(--accent);
  }
`;

const TableContainer = styled.div`
  background: var(--glass);
  backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: 16px;
  text-align: left;
  border-bottom: 1px solid var(--border);
  background: rgba(255, 255, 255, 0.02);
  color: var(--text);
`;

const Td = styled.td`
  padding: 16px;
  border-bottom: 1px solid var(--border);
  color: var(--text);
`;

const StatusBadge = styled.span`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${(props) =>
    props.status === "Approved"
      ? "rgba(16, 185, 129, 0.2)"
      : props.status === "Rejected"
        ? "rgba(239, 68, 68, 0.2)"
        : "rgba(245, 158, 11, 0.2)"};
  color: ${(props) =>
    props.status === "Approved"
      ? "#10b981"
      : props.status === "Rejected"
        ? "#ef4444"
        : "#f59e0b"};
`;

function LeaveRequest() {
  const [leaves, setLeaves] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [formData, setFormData] = useState({
    start_date: "",
    end_date: "",
    leave_type: "",
    reason: "",
  });

  const fetchMyLeaves = async () => {
    try {
      const empId = localStorage.getItem("employee_id");
      const res = await api.get(`${HR_BASE_URL}leaves/my-leaves/?employee_id=${empId}`);
      setLeaves(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchLeaveTypes = async () => {
    try {
      const res = await api.get(`${HR_BASE_URL}leave-types/`);
      const active = res.data.filter((lt) => lt.is_active);
      setLeaveTypes(active);
      setFormData((prev) => (prev.leave_type ? prev : { ...prev, leave_type: active[0]?.name || "" }));
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchMyLeaves();
    fetchLeaveTypes();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const empId = localStorage.getItem("employee_id");
      const empName = localStorage.getItem("name") || "";
      const empDept = localStorage.getItem("department_name") || "";
      const empDeptId = localStorage.getItem("department_id") || "";
      await api.post(`${HR_BASE_URL}leaves/apply/`, {
        ...formData,
        employee_id: empId,
        employee_name: empName,
        department: empDept,
        department_id: empDeptId,
      });
      alert("Leave requested successfully!");
      setFormData({ start_date: "", end_date: "", leave_type: leaveTypes[0]?.name || "", reason: "" });
      fetchMyLeaves();
    } catch (err) {
      console.error(err);
      alert("Failed to submit leave request");
    }
  };

  return (
    <Page>
      <Container>
        <Header>
          <Title>
            <FileText size={32} color="var(--primary)" />
            Leave Management
          </Title>
          <Subtitle>Apply for leave and view your history</Subtitle>
        </Header>

        <Grid>
          <FormCard>
            <h3 style={{ marginTop: 0 }}>Apply for Leave</h3>
            <form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>Leave Type</Label>
                <Select name="leave_type" value={formData.leave_type} onChange={handleChange}>
                  {leaveTypes.length === 0 && <option value="">No leave types configured</option>}
                  {leaveTypes.map((lt) => (
                    <option key={lt.id} value={lt.name}>{lt.name}</option>
                  ))}
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>Start Date</Label>
                <Input type="date" name="start_date" required value={formData.start_date} onChange={handleChange} />
              </FormGroup>
              <FormGroup>
                <Label>End Date</Label>
                <Input type="date" name="end_date" required value={formData.end_date} onChange={handleChange} />
              </FormGroup>
              <FormGroup>
                <Label>Reason</Label>
                <TextArea name="reason" required value={formData.reason} onChange={handleChange} placeholder="Briefly describe the reason for your leave..." />
              </FormGroup>
              <SubmitButton type="submit">
                <PlusCircle size={20} />
                Submit Request
              </SubmitButton>
            </form>
          </FormCard>

          <div>
            <h3 style={{ margin: "0 0 16px 0", display: "flex", alignItems: "center", gap: "8px" }}>
              <List size={24} />
              My Leaves
            </h3>
            <TableContainer>
              <Table>
                <thead>
                  <tr>
                    <Th>Type</Th>
                    <Th>From</Th>
                    <Th>To</Th>
                    <Th>Status</Th>
                  </tr>
                </thead>
                <tbody>
                  {leaves.map((leave) => (
                    <tr key={leave.id}>
                      <Td>{leave.leave_type}</Td>
                      <Td>{leave.start_date}</Td>
                      <Td>{leave.end_date}</Td>
                      <Td>
                        <StatusBadge status={leave.status}>{leave.status}</StatusBadge>
                      </Td>
                    </tr>
                  ))}
                  {leaves.length === 0 && (
                    <tr>
                      <Td colSpan="4" style={{ textAlign: "center", padding: "32px" }}>
                        No leave history found.
                      </Td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </TableContainer>
          </div>
        </Grid>
      </Container>
    </Page>
  );
}

export default LeaveRequest;
